import * as productApi from '@apis/product';
import { ProductModel } from '@models/product';
import { useProductsStore } from '@stores/proudctsStore';
import { useQuery } from '@tanstack/react-query';
import { getAddressText } from '@utils/dataTransform';
import { cdnImageUrl } from '@utils/image';
import { Button, Checkbox, Input, Modal, Popover, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

interface SelectProductModalProps {
  open?: boolean;
  onAddProducts: (products: ProductModel[]) => void;
  onClose?: () => void;
  selectLimit?: number;
  sellerId?: string;
  disabledProductIds?: string[];
}

export const SelectProductModal: React.FC<SelectProductModalProps> = (props) => {
  const [selectedProducts, setSelectedProducts] = useState<ProductModel[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [applyPeriodDiscountFilter, setApplyPeriodDiscountFilter] = useState<boolean>(false);
  const store = useProductsStore();
  const disabledProductIdSet = new Set(props.disabledProductIds || []);
  const isProductDisabled = (productId?: string) => !!productId && disabledProductIdSet.has(productId);
  const selectableProducts = (store.searchResultContainer.contents || []).filter(
    (product) => product?._id && !isProductDisabled(product._id),
  );

  const productSearchQuery = useQuery({
    queryKey: [
      `admin-search-products`,
      JSON.stringify({ ...store.searchQuery, status: 'open', displayStatus: 'open', applyPeriodDiscountFilter }),
    ],
    queryFn: () =>
      productApi.searchProducts({
        ...store.searchQuery,
        status: 'open',
        displayStatus: 'open',
        sellerId: props?.sellerId,
        ...(applyPeriodDiscountFilter ? { applyPeriodDiscount: true } : {}),
      }),
  });

  useEffect(() => {
    const searchResult = productSearchQuery.data?.data;
    if (searchResult) {
      store.setProductsContainer(searchResult);
    }
  }, [productSearchQuery.data?.data]);

  const PERIOD_DISCOUNT_COLUMN: ColumnsType<ProductModel>[number] = {
    title: '할인 기간',
    width: 200,
    render: (record: ProductModel) => {
      const { discountStartDate, discountEndDate } = record.discountInfo || {};
      if (!discountStartDate && !discountEndDate) return <span style={{ color: '#bbb' }}>-</span>;
      return (
        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
          {discountStartDate && <div>시작: {moment(discountStartDate).format('YYYY.MM.DD HH:mm')}</div>}
          {discountEndDate && (
            <div style={{ color: new Date(discountEndDate) < new Date() ? '#e53e3e' : '#38a169' }}>
              종료: {moment(discountEndDate).format('YYYY.MM.DD HH:mm')}
            </div>
          )}
        </div>
      );
    },
  };

  const TABLE_COLUMNS: ColumnsType<ProductModel> = [
    {
      fixed: 'left',
      title: () => {
        return (
          <Checkbox
            disabled={!!props.selectLimit || selectableProducts.length === 0}
            checked={
              selectableProducts.length > 0 &&
              JSON.stringify(selectedProducts.map((product) => product._id).sort()) ===
                JSON.stringify(selectableProducts.map((product) => product._id).sort())
            }
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedProducts(selectableProducts);
              } else {
                setSelectedProducts([]);
              }
            }}></Checkbox>
        );
      },
      width: 60,
      render: (record) => {
        return (
          <Checkbox
            disabled={
              isProductDisabled(record?._id) ||
              (!!props.selectLimit &&
                selectedProducts.length === props.selectLimit &&
                selectedProducts[0]?._id !== record?._id)
            }
            onChange={(e) => {
              setSelectedProducts((values) => {
                const checked = e.target.checked;
                const newValues = [...values];
                if (checked) {
                  if (!newValues.find((item) => item._id === record._id)) {
                    newValues.push(record);
                  }
                  return newValues;
                } else {
                  return newValues.filter((value) => value._id !== record._id);
                }
              });
            }}
            checked={!!selectedProducts.find((item) => item?._id === record?._id)}></Checkbox>
        );
      },
    },
    {
      title: '썸네일',
      dataIndex: 'thumbnailImage',
      render: (thumbnailImage) => (
        <img
          src={cdnImageUrl(thumbnailImage)}
          style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
        />
      ),
    },
    {
      title: '판매방식',
      width: 120,
      dataIndex: 'saleType',
      render: (saleType) => `${saleType}`,
    },
    {
      title: '구분',
      dataIndex: 'activityType',
      render: (activityType) => {
        if (!activityType) {
          return '-';
        }
        return `${activityType}`;
      },
    },
    {
      title: '지역',
      dataIndex: 'location',
      render: (location) => {
        let content = '';
        if (location?.specificAddress) {
          content = `${location?.specificAddress?.address} ${location?.specificAddress?.address2}`;
        }
        return (
          <Popover content={content}>
            <div style={{ color: content ? 'green' : 'black' }}>{getAddressText({ availableArea: location })}</div>
          </Popover>
        );
      },
    },
    {
      title: '판매자',
      dataIndex: 'sellerName',
      render: (sellerName) => {
        return `${sellerName}`;
      },
    },
    {
      title: '상픔명',
      width: 240,
      dataIndex: 'title',
      render: (title) => `${title}`,
    },
    {
      title: '금액',
      dataIndex: 'fee',
      render: (fee) => `${fee?.toLocaleString()}원`,
    },
    ...(applyPeriodDiscountFilter ? [PERIOD_DISCOUNT_COLUMN] : []),
    {
      title: '생성/수정',
      render: (record) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY.MM.DD HH:mm')}</div>
            <div style={{ fontWeight: 'bold' }}>{moment(record.updatedAt).format('YYYY.MM.DD HH:mm')}</div>
          </div>
        );
      },
    },
    {
      title: '선택',
      dataIndex: '_id',
      render: (_id, record) => {
        return (
          <Button
            key={_id}
            disabled={isProductDisabled(record._id)}
            onClick={() => {
              props.onAddProducts && props.onAddProducts([record]);
            }}>
            선택
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      open={props.open}
      footer={false}
      onCancel={props.onClose}
      onOk={props.onClose}
      width={1280}
      zIndex={1500}
      styles={{
        body: {
          height: '600px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}>
      <style>{`
        .select-product-disabled-row > td {
          background-color: #f5f5f5 !important;
          color: #bfbfbf !important;
        }
        .select-product-disabled-row:hover > td {
          background-color: #f0f0f0 !important;
        }
      `}</style>
      <div style={{ flexShrink: 0, marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            style={{ width: '240px' }}
            value={searchKeyword}
            placeholder={'검색어 입력 후 엔터'}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
            }}
            onKeyUp={(e) => {
              if (e.code === 'Enter' && searchKeyword) {
                store.updateSearchQuery({ keyword: searchKeyword });
                setSelectedProducts([]);
              }
            }}></Input>
          <Button
            onClick={() => {
              setSearchKeyword(undefined);
              store.resetSearchQuery();
              setSelectedProducts([]);
            }}>
            검색 초기화
          </Button>
          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            검색결과: {store.searchResultContainer.total ?? 0}건
          </div>
          <Checkbox
            checked={applyPeriodDiscountFilter}
            onChange={(e) => {
              setApplyPeriodDiscountFilter(e.target.checked);
              setSelectedProducts([]);
            }}>
            기간할인상품
          </Checkbox>
        </div>

        <div style={{ marginRight: '20px' }}>
          {(selectedProducts.filter((item) => item._id) || []).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div>{`${(selectedProducts.filter((item) => item._id) || []).length}개 선택 항목을`} </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button
                  onClick={() => {
                    props.onAddProducts &&
                      props.onAddProducts(selectedProducts.filter((item) => item._id && !isProductDisabled(item._id)));
                    setSelectedProducts([]);
                    props.onClose && props.onClose();
                  }}>
                  추가하기
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <Table
          scroll={{ x: 'max-content', y: 'calc(600px - 152px)' }}
          columns={TABLE_COLUMNS}
          rowKey={(record) => record?._id}
          rowClassName={(record) => (isProductDisabled(record._id) ? 'select-product-disabled-row' : '')}
          dataSource={store.searchResultContainer.contents.filter((product) => product?._id)}
          pagination={{
            current: (store.searchResultContainer.page || 0) + 1,
            pageSize: store.searchResultContainer.size || 20,
            total: store.searchResultContainer.total,
          }}
          loading={productSearchQuery.isLoading}
          onChange={(e) => {
            store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
          }}
        />
      </div>
    </Modal>
  );
};

export default SelectProductModal;
